import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import pick from 'lodash/pick';
import noop from 'lodash/noop';
import { FocusWithin } from 'react-focus-within';
import { Icon, Box, Flex, NakedButton } from 'roo-ui/components';
import { useDataLayer } from 'hooks/useDataLayer';
import { getQueryParams } from 'store/router/routerSelectors';
import { getPropertyLocation } from 'store/property/propertySelectors';
import createSearchQueryString from 'lib/search/createSearchQueryString';
import { DEFAULTS as SEARCH_DEFAULTS } from 'lib/enums/search';
import { QH_DEALS_URL, QH_LUXE_URL, QH_PACKAGES_URL, QCOM_LANDING_PAGE_URL, DESTINATIONS_AUSTRALIA } from 'config';
import { HeaderListItem, HeaderList, DropdownLink, StyledLink } from './primitives';
import DestinationsMenu from 'components/Header/HeaderDesktop/NavigationLinks/DestinationsMenu';
import AppLink from 'components/AppLink';

const SEARCH_DEFAULT_PARAM_KEYS = Object.keys(SEARCH_DEFAULTS);

const searchQueryString = ({ queryParams, propertyLocation }) => {
  const query = { location: propertyLocation, ...queryParams };
  return createSearchQueryString(pick(query, SEARCH_DEFAULT_PARAM_KEYS));
};

const HeaderLink = ({ text, hasSubMenu, setIsQffMenuOpen, setIsPhoneMenuOpen, ...rest }) => {
  const { emitInteractionEvent } = useDataLayer();

  const handleOnClick = useCallback(() => {
    setIsQffMenuOpen(false);
    setIsPhoneMenuOpen(false);

    if (hasSubMenu) return;

    emitInteractionEvent({ type: 'Site Navigation', value: `${text} Selected` });
  }, [emitInteractionEvent, hasSubMenu, setIsPhoneMenuOpen, setIsQffMenuOpen, text]);

  return (
    <StyledLink {...rest} onClick={handleOnClick}>
      <Flex>
        {text}
        {hasSubMenu && (
          <Box>
            <Icon name="expandMore" size={18} ml={2} />
          </Box>
        )}
      </Flex>
    </StyledLink>
  );
};

HeaderLink.propTypes = {
  text: PropTypes.string.isRequired,
  hasSubMenu: PropTypes.bool,
  setIsQffMenuOpen: PropTypes.func,
  setIsPhoneMenuOpen: PropTypes.func,
};
HeaderLink.defaultProps = {
  hasSubMenu: false,
  setIsQffMenuOpen: noop,
  setIsPhoneMenuOpen: noop,
};

const HeaderLinks = ({ setIsQffMenuOpen, setIsPhoneMenuOpen }) => {
  const queryParams = useSelector(getQueryParams);
  const propertyLocation = useSelector(getPropertyLocation);

  return (
    <HeaderList>
      <HeaderListItem>
        <HeaderLink
          setIsQffMenuOpen={setIsQffMenuOpen}
          setIsPhoneMenuOpen={setIsPhoneMenuOpen}
          text="Hotels"
          data-testid="hotels-link"
          as={AppLink}
          to={`/search/list?${searchQueryString({ queryParams, propertyLocation })}`}
        />
      </HeaderListItem>
      <FocusWithin>
        {({ focusProps, isFocused }) => (
          <HeaderListItem {...focusProps} className={isFocused ? 'open-dropdown-menu' : ''}>
            <DropdownLink display={['none', 'none', 'block']}>
              <HeaderLink text="Destinations" hasSubMenu as={NakedButton} data-testid="destinations-link" />
              <Box className="nav-dropdown-menu">
                <DestinationsMenu />
              </Box>
            </DropdownLink>
            <Box display={['block', 'block', 'none']}>
              <HeaderLink
                setIsQffMenuOpen={setIsQffMenuOpen}
                setIsPhoneMenuOpen={setIsPhoneMenuOpen}
                as={AppLink}
                to={DESTINATIONS_AUSTRALIA}
                text="Destinations"
                flexDirection="column"
              />
            </Box>
          </HeaderListItem>
        )}
      </FocusWithin>
      <HeaderListItem>
        <HeaderLink
          setIsQffMenuOpen={setIsQffMenuOpen}
          setIsPhoneMenuOpen={setIsPhoneMenuOpen}
          href={QH_DEALS_URL}
          data-testid="deals-link"
          text="Hotel Deals"
        />
      </HeaderListItem>
      <HeaderListItem>
        <HeaderLink
          setIsQffMenuOpen={setIsQffMenuOpen}
          setIsPhoneMenuOpen={setIsPhoneMenuOpen}
          href={QH_LUXE_URL}
          data-testid="luxe-link"
          text="Luxury Offers"
        />
      </HeaderListItem>
      <HeaderListItem>
        <HeaderLink
          setIsQffMenuOpen={setIsQffMenuOpen}
          setIsPhoneMenuOpen={setIsPhoneMenuOpen}
          href={QH_PACKAGES_URL}
          data-testid="holiday-packages-link"
          rel="noreferrer noopener"
          target="_blank"
          text="Holiday Packages"
        />
      </HeaderListItem>
      <HeaderListItem>
        <HeaderLink
          setIsQffMenuOpen={setIsQffMenuOpen}
          setIsPhoneMenuOpen={setIsPhoneMenuOpen}
          href={QCOM_LANDING_PAGE_URL}
          data-testid="airbnb-link"
          rel="noreferrer noopener"
          target="_blank"
          text="Airbnb"
        />
      </HeaderListItem>
    </HeaderList>
  );
};

HeaderLinks.propTypes = {
  setIsQffMenuOpen: PropTypes.func,
  setIsPhoneMenuOpen: PropTypes.func,
};

HeaderLinks.defaultProps = {
  setIsQffMenuOpen: noop,
  setIsPhoneMenuOpen: noop,
};

export default HeaderLinks;
